import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, Typography } from "@mui/material";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { RenderTransactionStatus } from "components/common/RenderTransactionStatus";
import { GET_PLATFORM_REWARDS_FUNDING } from "graphql/platform";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { formatCashFull } from "utils/formatCash";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import TransactionsType from "enums/TransactionsType";
import type { PlatformTransactionRow } from "./types";

interface Row {
  id: string;
  date: string;
  type: string;
  status: string;
  amount: string;
  description: string;
}

// The journal's own types, which the transactions renderer never shows
const TYPE_LABELS: Record<string, string> = {
  [TransactionsType.REWARDS_ACCRUAL]: "Rewards earned",
  [TransactionsType.BALANCE_TRANSFER]: "Balance transfer",
  [TransactionsType.REWARDS_TO_BANK]: "Rewards to bank",
  [TransactionsType.REWARDS_TO_BALANCE]: "Rewards to balance",
  [TransactionsType.INBOUND_TRANSFER]: "ACH In",
  [TransactionsType.OUTBOUND_TRANSFER]: "ACH Out",
};

interface RewardsFundingTableProps {
  platformAccountId: string;
  refreshDataIndex: number;
}

const RewardsFundingTable: React.FC<RewardsFundingTableProps> = ({
  platformAccountId,
  refreshDataIndex,
}) => {
  const { t } = useTranslation();

  const extractRows = (data: {
    [key: string]: { edges?: { node: PlatformTransactionRow }[] };
  }): Row[] => {
    const firstKey = Object.keys(data)[0];
    return (data[firstKey]?.edges ?? []).map(({ node }) => ({
      id: node.id,
      date: node.transactionDate
        ? new Date(node.transactionDate).toLocaleDateString()
        : "",
      type: t(TYPE_LABELS[node.type] ?? node.type),
      status: node.status,
      amount: formatCashFull(node.amount, node.currency),
      description: node.description ?? "",
    }));
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_PLATFORM_REWARDS_FUNDING,
      initialVariables: {
        first: DEFAULT_PAGINATION_SIZE,
        after: undefined,
        platformAccountId,
      },
      extractData: extractRows,
      refetchIndex: refreshDataIndex,
    });

  const columns: DataColumn<Row>[] = useMemo(
    () => [
      { id: "date", header: t("Date"), cell: (row) => row.date },
      { id: "type", header: t("Type"), cell: (row) => row.type },
      {
        id: "status",
        header: t("Status"),
        cell: (row) => (
          <RenderTransactionStatus transactionStatus={row.status} smallPill />
        ),
      },
      {
        id: "amount",
        header: t("Amount"),
        align: "right",
        cell: (row) => row.amount,
      },
      {
        id: "description",
        header: t("Description"),
        cell: (row) => row.description,
      },
    ],
    [t],
  );

  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        minWidth: 0,
        padding: "var(--space-5)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h4">{t("Funding history")}</Typography>
      <DataTable<Row>
        columns={columns}
        rows={dataArray}
        getRowKey={(row) => row.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onScroll={handleScroll}
        maxHeight={350}
        emptyState={t("No funding history yet")}
        loadingMoreText={t("Loading more history")}
        stickyHeader
      />
    </Card>
  );
};

export default RewardsFundingTable;
